import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Download } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL, RERA } from "@/lib/constants";
import { cn } from "@/lib/utils";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const sections = NAV_LINKS.map((n) => document.getElementById(n.id));
      const y = window.scrollY + 180;
      let current = "";
      sections.forEach((s) => {
        if (s && s.offsetTop <= y) current = s.id;
      });
      setActive(current);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDownloadBrochure = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = '/brochure_palaash_paradise.pdf';
    link.download = 'brochure_palaash_paradise.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
          scrolled
            ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        )}
        data-testid="site-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => go("hero")}
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 border border-[#D4AF37]/60 flex items-center justify-center font-serif text-[#D4AF37] text-xl tracking-tight group-hover:border-[#D4AF37] transition-colors">
              P
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]">Project By</div>
              <div className="font-serif text-[#F8F6F0] text-[20px]">Prachi Infra</div>
            </div>
          </button>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-${l.id}-link`}
                className={cn(
                  "text-sm tracking-wide transition-colors relative group",
                  active === l.id ? "text-[#D4AF37]" : "text-[#A0A0A0] hover:text-[#F8F6F0]"
                )}
              >
                {l.label}
                <span className={cn(
                  "absolute -bottom-1 left-0 h-px bg-[#D4AF37] transition-all duration-500",
                  active === l.id ? "w-full" : "w-0 group-hover:w-full"
                )} />
              </button>
            ))}
            {/* Brochure Nav Item */}
            <button
              onClick={handleDownloadBrochure}
              className="text-sm tracking-wide transition-colors relative group text-[#A0A0A0] hover:text-[#F8F6F0] flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Brochure
              <span className="absolute -bottom-1 left-0 h-px bg-[#D4AF37] transition-all duration-500 w-0 group-hover:w-full" />
            </button>
          </nav>

          {/* RERA badge + Call */}
          <div className="hidden md:flex items-center gap-5">
            <div className="text-right leading-tight">
              <div className="text-[13px] uppercase tracking-[0.3em] text-[#A0A0A0]">MahaRERA</div>
              <div className="font-mono text-[15px] text-[#D4AF37]">{RERA}</div>
            </div>
            <a
              href={PHONE_TEL}
              data-testid="nav-call-button"
              className="btn-luxury inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F3E5AB] text-black px-2 py-1.5 text-[16px] font-medium tracking-wide transition-colors"
            >
              <Phone className="w-5 h-5" />
              {PHONE_DISPLAY}
            </a>
          </div>

          {/* Mobile menu trigger */}
          <button
            className="lg:hidden text-[#F8F6F0] p-2"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </motion.header>

      {/* Mobile sheet */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#050505]/95 backdrop-blur-2xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/5">
              <div className="font-serif text-[#D4AF37] text-lg">Palaash Paradise</div>
              <button
                onClick={() => setOpen(false)}
                className="text-[#F8F6F0] p-2"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <motion.nav
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.06 } } }}
              className="flex flex-col px-6 py-10 gap-1"
            >
              {NAV_LINKS.map((l) => (
                <motion.button
                  key={l.id}
                  variants={{ hidden: { opacity: 0, x: -20 }, show: { opacity: 1, x: 0 } }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="font-serif text-3xl text-left text-[#F8F6F0] hover:text-[#D4AF37] transition-colors py-3 border-b border-white/5"
                >
                  {l.label}
                </motion.button>
              ))}
              {/* Mobile Brochure Item */}
              <motion.button
                variants={{ hidden: { opacity: 0, x: -20 }, show: { opacity: 1, x: 0 } }}
                onClick={handleDownloadBrochure}
                className="font-serif text-3xl text-left text-[#F8F6F0] hover:text-[#D4AF37] transition-colors py-3 border-b border-white/5 flex items-center gap-3"
              >
                <Download className="w-6 h-6" />
                Brochure
              </motion.button>
              <a
                href={PHONE_TEL}
                data-testid="mobile-nav-call"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-6 py-4 text-base font-medium tracking-wide"
              >
                <Phone className="w-5 h-5" />
                Call Now — {PHONE_DISPLAY}
              </a>
              <div className="mt-6 text-center text-xs text-[#A0A0A0] tracking-[0.3em] uppercase">
                MahaRERA <span className="text-[#D4AF37] ml-2">{RERA}</span>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};